/**
 * Source Mapping Utility for Onboarding Flow Decisions
 * 
 * This utility maps various source inputs to standardized source categories
 * and determines whether a source should trigger simplified or full onboarding flow.
 * 
 * 📋 ADDING NEW SOURCES:
 * See detailed guide: ./ADDING_NEW_SOURCES_GUIDE.md
 * Quick checklist: ./NEW_SOURCE_CHECKLIST.md
 *
 * Partner signup URL codes (e.g. /register/signup/SAIA) are NOT listed here.
 * Add those in System > Signup Codes (DB-managed via signup_source_code).
 * 
 * @author Zavior Team
 * @version 2.0.0 - Updated with organization sources and removed invitation logic
 */

/**
 * Maps various source inputs to standardized source categories
 * 
 * Handles various input formats like "GoogleSearch", "google_search", "Google Search", etc.
 * and normalizes them to consistent category names.
 * 
 * @param {string} rawSource - The raw source string from URL parameters, UTM, or referrer
 * @returns {string} Standardized source category name
 * 
 * @example
 * mapSourceToCategory('GoogleSearch') // Returns 'Google Search'
 * mapSourceToCategory('facebook') // Returns 'Social Media'
 */
export const mapSourceToCategory = (rawSource) => {
  if (!rawSource) return 'Unknown';
  
  const source = rawSource.toLowerCase().trim();
  
  // Simplified flow sources - Search & Discovery
  const searchSources = {
    'google': 'Google Search',
    'googlesearch': 'Google Search',
    'google_search': 'Google Search',
    'seo': 'SEO',
    'search': 'Google Search'
  };
  
  // Simplified flow sources - Social Media
  const socialSources = {
    'facebook': 'Social Media',
    'fb': 'Social Media',
    'linkedin': 'Social Media',
    'twitter': 'Social Media',
    'instagram': 'Social Media',
    'social': 'Social Media',
    'socialmedia': 'Social Media',
    'social_media': 'Social Media'
  };
  
  // Simplified flow sources - Marketing
  const marketingSources = {
    'advertisement': 'Advertisement',
    'ads': 'Advertisement',
    'googleads': 'Advertisement',
    'facebookads': 'Advertisement',
    'ad': 'Advertisement',
    'landingpage': 'Landing Page',
    'landing_page': 'Landing Page',
    'landing': 'Landing Page',
    'blog': 'Blog',
    'emailmarketing': 'Email Marketing',
    'email_marketing': 'Email Marketing',
    'newsletter': 'Email Marketing',
    'email': 'Email Marketing'
  };
  
  // Simplified flow sources - Content
  const contentSources = {
    'youtube': 'YouTube',
    'podcast': 'Podcast',
    'video': 'YouTube'
  };
  
  // Simplified flow sources - Outreach
  const outreachSources = {
    'coldoutreach': 'Cold Outreach',
    'cold_outreach': 'Cold Outreach',
    'cold_email': 'Cold Outreach',
    'cold_call': 'Cold Outreach',
    'coldemail': 'Cold Outreach',
    'coldcall': 'Cold Outreach'
  };
  
  // Full flow sources - Professional
  const professionalSources = {
    'referral': 'Referral',
    'partner': 'Partner',
    'consultant': 'Consultant'
  };
  
  // Full flow sources - Enterprise
  const enterpriseSources = {
    'enterprisesales': 'Enterprise Sales',
    'enterprise_sales': 'Enterprise Sales',
    'enterprise': 'Enterprise Sales',
    'demorequest': 'Demo Request',
    'demo_request': 'Demo Request',
    'demo': 'Demo Request'
  };
  
  // Full flow sources - Events
  const eventSources = {
    'webinar': 'Webinar',
    'conference': 'Conference',
    'event': 'Conference'
  };
  
  // Full flow sources - Technical
  const technicalSources = {
    'integrationpartner': 'Integration Partner',
    'integration_partner': 'Integration Partner',
    'integration': 'Integration Partner',
    'reseller': 'Reseller'
  };
  
  // Check all source categories in order
  const allSources = {
    ...searchSources,
    ...socialSources,
    ...marketingSources,
    ...contentSources,
    ...outreachSources,
    ...professionalSources,
    ...enterpriseSources,
    ...eventSources,
    ...technicalSources
  };
  
  // Direct match
  if (allSources[source]) {
    return allSources[source];
  }
  
  // Partial matching for more flexible input handling
  for (const [key, value] of Object.entries(allSources)) {
    if (source.includes(key) || key.includes(source)) {
      return value;
    }
  }
  
  // Default to Unknown (which triggers simplified flow)
  return 'Unknown';
};

/**
 * Determines if a source should trigger the simplified onboarding flow
 * 
 * @param {string} source - Standardized source category from mapSourceToCategory
 * @returns {boolean} True if source should trigger simplified flow, false for full flow
 * 
 * @example
 * isSimplifiedSource('Google Search') // Returns true
 * isSimplifiedSource('Referral') // Returns false
 */
export const isSimplifiedSource = (source) => {
  const simplifiedSources = [
    // Search & Discovery
    'Google Search',
    'SEO',
    
    // Social Media
    'Social Media',
    
    // Marketing
    'Advertisement',
    'Landing Page',
    'Blog',
    'Email Marketing',
    
    // Content
    'YouTube',
    'Podcast',
    
    // Outreach
    'Cold Outreach',
    
    // Unknown sources default to simplified
    'Unknown'
  ];
  
  return simplifiedSources.includes(source);
};

/**
 * Gets all available source categories organized by flow type
 * 
 * @returns {Object} Object containing simplified and full flow source arrays
 */
export const getSourceCategories = () => {
  return {
    simplified: [
      'Google Search',
      'SEO',
      'Social Media',
      'Advertisement',
      'Landing Page',
      'Blog',
      'Email Marketing',
      'YouTube',
      'Podcast',
      'Cold Outreach',
      'Unknown'
    ],
    full: [
      'Referral',
      'Partner',
      'Consultant',
      'Enterprise Sales',
      'Demo Request',
      'Webinar',
      'Conference',
      'Integration Partner',
      'Reseller'
    ]
  };
};

/**
 * Gets user-friendly descriptions for source categories
 * 
 * @param {string} source - Standardized source category
 * @returns {string} Human-readable description of the source
 */
export const getSourceDescription = (source) => {
  const descriptions = {
    // Simplified Flow Descriptions
    'Google Search': 'Organic search discovery',
    'SEO': 'Search engine optimization',
    'Social Media': 'Social platform discovery',
    'Advertisement': 'Paid advertising campaigns',
    'Landing Page': 'Marketing landing page',
    'Blog': 'Content marketing discovery',
    'Email Marketing': 'Marketing email campaigns',
    'YouTube': 'Video content discovery',
    'Podcast': 'Audio content discovery',
    'Cold Outreach': 'Direct marketing outreach',
    
    // Full Flow Descriptions
    'Referral': 'Personal or professional referral',
    'Partner': 'Business partnership',
    'Consultant': 'Professional consultant recommendation',
    'Enterprise Sales': 'Direct enterprise sales contact',
    'Demo Request': 'Product demonstration request',
    'Webinar': 'Educational webinar attendance',
    'Conference': 'Industry conference or event',
    'Integration Partner': 'Technical integration partnership',
    'Reseller': 'Channel partner or reseller',
    
    'Unknown': 'Source not identified'
  };
  
  return descriptions[source] || 'Unknown source type';
};

/**
 * Validates if a source string is properly formatted
 * 
 * @param {string} source - Source string to validate
 * @returns {boolean} True if source is valid, false otherwise
 */
export const validateSource = (source) => {
  if (!source || typeof source !== 'string') {
    return false;
  }
  
  // Check if source is not empty and contains valid characters
  const trimmedSource = source.trim();
  return trimmedSource.length > 0 && /^[a-zA-Z0-9_\-\s]+$/.test(trimmedSource);
};

/**
 * Gets example URLs for testing different sources
 * 
 * @returns {Object} Object containing example URLs for each source type
 */
export const getSourceExamples = () => {
  return {
    simplified: {
      // Search & Discovery
      'Google Search': [
        '/register/signup/GoogleSearch',
        '/register/signup?source=google',
        '/register/signup?utm_source=google'
      ],
      'SEO': [
        '/register/signup/SEO',
        '/register/signup?source=seo',
        '/register/signup?utm_source=seo'
      ],
      
      // Social Media
      'Social Media': [
        '/register/signup/facebook',
        '/register/signup?source=linkedin',
        '/register/signup?utm_source=social'
      ],
      
      // Marketing
      'Advertisement': [
        '/register/signup/advertisement',
        '/register/signup?source=ads',
        '/register/signup?utm_source=googleads'
      ],
      'Landing Page': [
        '/register/signup/landingpage',
        '/register/signup?source=landing',
        '/register/signup?utm_source=landing_page'
      ],
      'Blog': [
        '/register/signup/blog',
        '/register/signup?source=blog',
        '/register/signup?utm_source=blog'
      ],
      'Email Marketing': [
        '/register/signup/emailmarketing',
        '/register/signup?source=newsletter',
        '/register/signup?utm_source=email'
      ],
      
      // Content
      'YouTube': [
        '/register/signup/youtube',
        '/register/signup?source=youtube',
        '/register/signup?utm_source=video'
      ],
      'Podcast': [
        '/register/signup/podcast',
        '/register/signup?source=podcast',
        '/register/signup?utm_source=podcast'
      ],
      
      // Outreach
      'Cold Outreach': [
        '/register/signup/coldoutreach',
        '/register/signup?source=cold_email',
        '/register/signup?utm_source=coldcall'
      ],
      
      // Unknown
      'Unknown': [
        '/register/signup/unknown',
        '/register/signup?source=random',
        '/register/signup?utm_source=mystery'
      ]
    },
    full: {
      // Professional
      'Referral': [
        '/register/signup/referral',
        '/register/signup?source=referral',
        '/register/signup?utm_source=referral'
      ],
      'Partner': [
        '/register/signup/partner',
        '/register/signup?source=partner',
        '/register/signup?utm_source=partner'
      ],
      'Consultant': [
        '/register/signup/consultant',
        '/register/signup?source=consultant',
        '/register/signup?utm_source=consultant'
      ],
      
      // Enterprise
      'Enterprise Sales': [
        '/register/signup/enterprisesales',
        '/register/signup?source=enterprise',
        '/register/signup?utm_source=enterprise_sales'
      ],
      'Demo Request': [
        '/register/signup/demorequest',
        '/register/signup?source=demo',
        '/register/signup?utm_source=demo_request'
      ],
      
      // Events
      'Webinar': [
        '/register/signup/webinar',
        '/register/signup?source=webinar',
        '/register/signup?utm_source=webinar'
      ],
      'Conference': [
        '/register/signup/conference',
        '/register/signup?source=conference',
        '/register/signup?utm_source=event'
      ],
      
      // Technical
      'Integration Partner': [
        '/register/signup/integrationpartner',
        '/register/signup?source=integration',
        '/register/signup?utm_source=integration_partner'
      ],
      'Reseller': [
        '/register/signup/reseller',
        '/register/signup?source=reseller',
        '/register/signup?utm_source=reseller'
      ]
    }
  };
};
